// frontend/src/profile/UserManagement.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge } from 'react-bootstrap';
import {
  FaUser, FaEnvelope, FaLock, FaUserTag, FaBuilding,
  FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaUsers,
  FaFilter, FaSearch, FaTimes, FaShieldAlt, FaCheckCircle,
  FaUserShield, FaUserFriends, FaListAlt
} from 'react-icons/fa';
import './UserManagement.css';
import '../styles/AdminTabs.css';
import { API_URL, API_ENDPOINTS } from '../config/api';

function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'User',
    apartment: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('');

  // Estado para tabs
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  // 🔥 FILTRO CORREGIDO - Sin error de toLowerCase
  const filteredUsers = users.filter(usr => {
    // Protección contra valores null/undefined
    const name = usr.name || '';
    const username = usr.username || '';
    const email = usr.email || '';
    const apartment = usr.apartment ? usr.apartment.toString() : '';

    const matchesSearch = searchTerm === '' ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apartment.includes(searchTerm);

    // Filtro por tab activo
    const matchesTab = activeTab === 'all' ||
      (activeTab === 'admins' && usr.role === 'Admin') ||
      (activeTab === 'users' && usr.role === 'User');

    const matchesRole = roleFilter === 'all' || usr.role === roleFilter;
    const matchesDept = departmentFilter === '' || apartment.includes(departmentFilter);

    return matchesSearch && matchesTab && matchesRole && matchesDept;
  });

  // Contadores para los tabs
  const adminCount = users.filter(u => u.role === 'Admin').length;
  const userCount = users.filter(u => u.role === 'User').length;

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setDepartmentFilter('');
  };

  const apiRequest = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
    const config = {
      ...options,
      headers: { ...defaultHeaders, ...options.headers }
    };
    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error en API request:', error);
      throw error;
    }
  };

  const fetchUsers = async () => {
    setError(null);
    try {
      const response = await apiRequest(API_ENDPOINTS.users);
      setUsers(response.users || []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError(err.message || 'Error al cargar usuarios');
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    resetForm();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowAdminWarning(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'User',
      apartment: ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setFormData(prev => ({ ...prev, role: newRole }));
    setShowAdminWarning(newRole === 'Admin');
  };

  const validateForm = () => {
    if (!formData.name || !formData.username || !formData.email || !formData.password) {
      setError('Todos los campos son obligatorios');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Email inválido');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(API_ENDPOINTS.createUser, {
        method: 'POST',
        body: JSON.stringify({
          name: formData.name,
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          apartment: formData.apartment
        })
      });
      console.log('Usuario creado:', response);
      setSuccess('Usuario creado exitosamente');
      fetchUsers();
      setTimeout(() => { handleCloseModal(); }, 1500);
    } catch (err) {
      console.error('Error al crear usuario:', err);
      setError(err.message || 'Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    try {
      const response = await apiRequest(`${API_ENDPOINTS.users}/${userId}`, {
        method: 'DELETE'
      });
      console.log('Usuario eliminado:', response);
      setSuccess('Usuario eliminado exitosamente');
      fetchUsers();
      setTimeout(() => { setSuccess(null); }, 3000);
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      setError(err.message || 'Error al eliminar usuario');
    }
  };

  return (
    <div className="urbanq-users-container">
      {/* Tabs de navegación */}
      <div className="urbanq-admin-tabs">
        <button
          className={`urbanq-admin-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <FaListAlt /> Todos ({users.length})
        </button>
        <button
          className={`urbanq-admin-tab ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
        >
          <FaUserShield /> Administradores ({adminCount})
        </button>
        <button
          className={`urbanq-admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUserFriends /> Usuarios ({userCount})
        </button>
      </div>

      {/* Contenido principal */}
      <div className="urbanq-users-content">
        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="modern-alert">
            <FaExclamationTriangle className="me-2" />{error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="modern-alert">
            <FaCheckCircle className="me-2" />{success}
          </Alert>
        )}

      {/* Sección de Filtros - estilo urbanq-support-section */}
      <div className="urbanq-users-section search-filters-section">
        <div className="filters-header">
          <div className="filters-title">
            <FaFilter className="me-2" />
            <h3>Búsqueda y Filtros</h3>
          </div>
          {(searchTerm || roleFilter !== 'all' || departmentFilter) && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <FaTimes className="me-2" />
              Limpiar filtros
            </button>
          )}
        </div>
        
        <div className="filters-grid">
          <div className="filter-item">
            <label className="filter-label">
              <FaSearch className="me-2" />
              Buscar Usuario
            </label>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="modern-input"
                placeholder="Nombre, usuario, email o depto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <span className="search-count">{filteredUsers.length} resultado(s)</span>
              )}
            </div>
          </div>

          <div className="filter-item">
            <label className="filter-label">
              <FaUserTag className="me-2" />
              Filtrar por Rol
            </label>
            <select 
              className="modern-select" 
              value={roleFilter} 
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">Todos los Roles</option>
              <option value="Admin">Solo Administradores</option>
              <option value="User">Solo Usuarios</option>
            </select>
          </div>

          <div className="filter-item">
            <label className="filter-label">
              <FaBuilding className="me-2" />
              Departamento
            </label>
            <input
              type="text"
              className="modern-input"
              placeholder="Ej: 101, 204..."
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            />
          </div>
        </div>

        {/* Active Filters Tags */}
        {(searchTerm || roleFilter !== 'all' || departmentFilter) && (
          <div className="active-filters">
            <span className="filters-label">Filtros activos:</span>
            {searchTerm && (
              <Badge bg="primary" className="filter-badge">
                Búsqueda: "{searchTerm}"
              </Badge>
            )}
            {roleFilter !== 'all' && (
              <Badge bg="info" className="filter-badge">
                Rol: {roleFilter}
              </Badge>
            )}
            {departmentFilter && (
              <Badge bg="warning" className="filter-badge">
                Depto: {departmentFilter}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Lista de Usuarios - estilo urbanq-support-section */}
      <div className="urbanq-users-section users-table-container">
        <div className="table-header">
          <h3 className="table-title">
            <FaUsers className="me-2" />
            Listado de Usuarios
            <Badge bg="dark" className="ms-2 count-badge">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'USUARIO' : 'USUARIOS'}
            </Badge>
            {filteredUsers.length !== users.length && (
              <Badge bg="secondary" className="ms-2">de {users.length} total</Badge>
            )}
          </h3>
          <button className="create-user-btn" onClick={handleOpenModal}>
            <FaPlus className="me-2" />
            Crear Nuevo Usuario
          </button>
        </div>

        <div className="modern-table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>NOMBRE</th>
                <th>USUARIO</th>
                <th>EMAIL</th>
                <th>DEPARTAMENTO</th>
                <th>ROL</th>
                <th>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan="7">
                    {users.length === 0 ? (
                      <div className="empty-state">
                        <FaUsers className="empty-icon" />
                        <p className="empty-text">No hay usuarios registrados</p>
                        <button className="create-user-btn-alt" onClick={handleOpenModal}>
                          <FaPlus className="me-2" />
                          Crear primer usuario
                        </button>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <FaExclamationTriangle className="empty-icon warning" />
                        <p className="empty-text">No se encontraron usuarios con los filtros aplicados</p>
                        <button className="clear-btn-alt" onClick={clearFilters}>
                          Limpiar filtros
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((usr) => (
                  <tr key={usr.id} className="table-row">
                    <td className="id-cell">{usr.id}</td>
                    <td className="name-cell">
                      <div className="user-avatar-cell">
                        <div className="mini-avatar">
                          {(usr.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <span>{usr.name || 'Sin nombre'}</span>
                      </div>
                    </td>
                    <td className="username-cell">{usr.username || '-'}</td>
                    <td className="email-cell">{usr.email || '-'}</td>
                    <td className="apartment-cell">
                      {usr.apartment ? (
                        <Badge bg="light" text="dark" className="dept-badge">
                          {usr.apartment}
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="role-cell">
                      <Badge 
                        bg={usr.role === 'Admin' ? 'danger' : 'info'} 
                        className="role-badge-table"
                      >
                        {usr.role || 'User'}
                      </Badge>
                    </td>
                    <td className="actions-cell">
                      <button 
                        className="action-btn edit-btn" 
                        disabled
                        title="Editar (próximamente)"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="action-btn delete-btn" 
                        onClick={() => handleDelete(usr.id)}
                        disabled={usr.id === user?.id}
                        title={usr.id === user?.id ? "No puedes eliminarte a ti mismo" : "Eliminar usuario"}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Crear Usuario */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg"
        className="modern-modal"
        centered
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <FaUserTag className="me-2" />
            Crear Nuevo Usuario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {showAdminWarning && (
            <Alert variant="danger" className="admin-warning-alert">
              <div className="warning-header">
                <FaExclamationTriangle className="warning-icon" />
                <h5>⚠️ ADVERTENCIA CRÍTICA DE SEGURIDAD ⚠️</h5>
              </div>
              <p className="warning-intro">
                Está a punto de crear un usuario con <strong>PRIVILEGIOS DE ADMINISTRADOR TOTAL</strong>.
              </p>
              <div className="warning-details">
                <h6>Este usuario tendrá acceso COMPLETO para:</h6>
                <ul>
                  <li>✓ <strong>Crear, modificar y eliminar usuarios</strong> (incluidos otros administradores)</li>
                  <li>✓ <strong>Acceder a datos financieros</strong> de todos los residentes</li>
                  <li>✓ <strong>Modificar pagos y alícuotas</strong> sin restricciones</li>
                  <li>✓ <strong>Ver información personal sensible</strong> de todos los usuarios</li>
                  <li>✓ <strong>Cambiar configuraciones críticas</strong> del sistema</li>
                </ul>
              </div>
              <div className="warning-footer">
                <strong>✋ RECOMENDACIÓN:</strong> Solo otorgue estos privilegios a personas de <strong>absoluta confianza</strong>.
              </div>
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <div className="form-grid">
              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaUser className="me-2" />
                  Nombre Completo *
                </Form.Label>
                <Form.Control 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleInputChange} 
                  placeholder="Ej: Juan Pérez" 
                  className="modern-form-input"
                  required 
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaUserTag className="me-2" />
                  Nombre de Usuario *
                </Form.Label>
                <Form.Control 
                  type="text" 
                  name="username" 
                  value={formData.username} 
                  onChange={handleInputChange} 
                  placeholder="Ej: jperez" 
                  className="modern-form-input"
                  required 
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaEnvelope className="me-2" />
                  Email *
                </Form.Label>
                <Form.Control 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  placeholder="usuario@ejemplo.com" 
                  className="modern-form-input"
                  required 
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaBuilding className="me-2" />
                  Departamento
                </Form.Label>
                <Form.Control 
                  type="text" 
                  name="apartment" 
                  value={formData.apartment} 
                  onChange={handleInputChange} 
                  placeholder="Ej: 101" 
                  className="modern-form-input"
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaLock className="me-2" />
                  Contraseña *
                </Form.Label>
                <Form.Control 
                  type="password" 
                  name="password" 
                  value={formData.password} 
                  onChange={handleInputChange} 
                  placeholder="Mínimo 6 caracteres" 
                  className="modern-form-input"
                  required 
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaLock className="me-2" />
                  Confirmar Contraseña *
                </Form.Label>
                <Form.Control 
                  type="password" 
                  name="confirmPassword" 
                  value={formData.confirmPassword} 
                  onChange={handleInputChange} 
                  placeholder="Repetir contraseña" 
                  className="modern-form-input"
                  required 
                />
              </Form.Group>

              <Form.Group className="form-group-modern full-width">
                <Form.Label>
                  <FaUserTag className="me-2" />
                  Rol *
                </Form.Label>
                <Form.Select 
                  name="role" 
                  value={formData.role} 
                  onChange={handleRoleChange}
                  className="modern-form-select"
                >
                  <option value="User">Usuario</option>
                  <option value="Admin">Administrador</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="modal-actions">
              <Button variant="secondary" onClick={handleCloseModal} className="btn-cancel">
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
                className="btn-submit"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creando...
                  </>
                ) : (
                  <>
                    <FaPlus className="me-2" />
                    Crear Usuario
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      </div>{/* Cierre urbanq-users-content */}
    </div>
  );
}

export default UserManagement;